import React, { useCallback, useEffect, useRef } from 'react';
import ContentBodyRenderer from 'components/Dashboard/ContentControl/ContentBodyRenderer';
import SkeletonKeyUpdatesContent from 'components/Dashboard/KeyUpdates/SkeletonKeyUpdatesContent';
import MenuItems from './MenuItems';
import TransformFromPascalCase from 'components/Settings/Utilities/TransformFromPascalCase';
import normalizeString from 'components/Dashboard/utilities/normalizeString';
import EmbedList from './EmbedList';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import { hex_is_light } from 'components/Reports/ui/ReportDesignButton';
import { getOS } from 'utils/getOS';
import { TitleWidget } from 'components/EmbedAnalytics';
import { useSiteSectionsNames } from 'utils/getAppSite';
import ParliamentLiveEventContent from 'components/Dashboard/ParliamentLive/ParliamentLiveEventContent';

const useSubtextOneResource = () => {
  const { getName } = useSiteSectionsNames();

  const subtextOneResource = (finalResources) => {
    let text = getName({ property: TransformFromPascalCase(finalResources[0]) })?.toLowerCase();
    if (text) {
      switch (text) {
        case 'x posts':
          return 'Showing the latest X posts';
        case 'events':
          return 'Showing upcoming events';
        default:
          return `Showing the ${text?.startsWith('latest') ? '' : 'latest'} ${text}`;
      }
    }
  };
  return { subtextOneResource };
};

const Embed = (props) => {
  const {
    resources,
    isLoading,
    results,
    topicName,
    content,
    design,
    header,
    widgetKeywordsLists,
    topicState,
    isFromPopup,
    selectedTopics,
    userId,
    includeBrandingProperty,
    hideResourcesWithoutContent,
  } = props;
  const { headerColour, background } = design;
  const { headerText, subheaderText } = header;
  const { topic, setTopic } = topicState;
  const finalResources = resources.split(',');
  const utmLinks = () => {
    if (!!results && Object.keys(results).length > 0) {
      return `?utm_source=${normalizeString(results.teamName)}-${normalizeString(
        topicName
      )}-embed-widget&utm_medium=referrer`;
    } else {
      return '';
    }
  };

  const { agencyUser } = useTopicOrClientWord();
  const embedElement = useRef(null);
  return (
    <>
      <div className='embed-content dashboard-root-container' ref={embedElement}>
        <div style={{ background: headerColour, color: hex_is_light(headerColour) ? '#00122b' : '#fff' }}>
          <TitleWidget
            isFromPopup={isFromPopup}
            selectedTopics={selectedTopics}
            topic={topic}
            setTopic={setTopic}
            analyticsKeywordsLists={widgetKeywordsLists}
            agencyUser={agencyUser}
            header={headerText}
            subheader={subheaderText}
            userId={userId}
          />
        </div>

        {content === null && (
          <>
            {finalResources.length > 1 && <MenuItems {...props} updatePosition={!hideResourcesWithoutContent} />}
            <EmbedList
              background={background}
              includeBrandingProperty={includeBrandingProperty}
              topic={topic}
              {...props}
            />
          </>
        )}
        {content !== null && <EmbedContentComponent utmLinks={utmLinks} {...props} />}
        {!isLoading && includeBrandingProperty && (
          <div className='embed-padding text-right embed-link-bottom' style={{ backgroundColor: background }}>
            <a
              className='embed-link'
              href={`${import.meta.env.VITE_APP_APPURL}${utmLinks()}`}
              target='_blank'
              rel='noopener noreferrer'
              style={{ color: hex_is_light(background) ? '#00122b' : '#fff' }}
            >
              Political updates powered by
              <img
                src={`${import.meta.env.VITE_APP_CDNURL}/images/logo${hex_is_light(background) ? '' : '_white'}.svg`}
                alt='PolicyMogul logo'
                width={'90px'}
                className={`d-inline-block ml-1`}
              />{' '}
            </a>
          </div>
        )}
      </div>
    </>
  );
};

const EmbedContentComponent = (props) => {
  const {
    typeOfContent,
    content,
    setContent,
    scrollbarContentContainer,
    isFromPopup,
    results,
    utmLinks,
    includeBrandingProperty,
    specificContentApiCall,
  } = props;
  const contentContainer = useRef(null);

  const embedContent = useCallback(
    (node) => {
      if (node !== null) {
        let links = document.querySelectorAll('.embed-content-container a');
        links.forEach((item) => {
          item.href += utmLinks();
          item.setAttribute('target', '_blank');
        });
      }
    },
    [results]
  );

  const callSpecificApiCall = useRef(null);
  callSpecificApiCall.current = (source) => {
    try {
      if (!!content?.item) {
        specificContentApiCall({ item: content?.item, source });
      }
    } catch (error) {}
  };

  useEffect(() => {
    const source = new AbortController();
    callSpecificApiCall.current(source);
    return () => {
      source.abort('Embed content loading cancelled by the user');
    };
  }, [includeBrandingProperty]);

  const createBackText = () => {
    switch (typeOfContent) {
      case 'LibraryArticles':
        return 'Library material';
      case 'RealtimeUtterances':
        return 'Parliament live';
      default:
        return TransformFromPascalCase(typeOfContent);
    }
  };

  return (
    <div className='flex-grow-1 dashboard-root-container'>
      <div className='embed-padding menu-embed-items embed-back-container mb-0'>
        <button
          className='general-button embed-button-back'
          onClick={() => {
            setContent(null);
          }}
        >
          Back to {createBackText()}
        </button>
      </div>
      {content.isLoading ? (
        <SkeletonKeyUpdatesContent />
      ) : (
        <>
          <div className='embed-content-container flex-grow-1 main-update-content-container' ref={embedContent}>
            {typeOfContent === 'RealtimeUtterances' ? (
              <>
                {(() => {
                  const currentNew = content.currentNew;
                  return (
                    <ParliamentLiveEventContent
                      key={`${typeOfContent}-content`}
                      eventItem={currentNew?.eventItem}
                      eventBodiesState={{
                        eventBodies: currentNew?.eventBodies,
                        setEventBodies: (eventBodies) =>
                          setContent({ ...content, currentNew: { ...content?.currentNew, eventBodies } }),
                      }}
                      embed
                    />
                  );
                })()}
              </>
            ) : (
              <>
                <div ref={contentContainer} />
                <CustomScrollbar
                  ref={scrollbarContentContainer}
                  className={`simple-scrollbar ${getOS() === 'Windows' ? 'title-widget-lists' : ''}`}
                  maximalThumbYSize={100}
                >
                  <div className='px-3'>
                    {(() => {
                      const currentNew = content.currentNew;
                      return (
                        <ContentBodyRenderer
                          contentType={typeOfContent}
                          currentNew={currentNew}
                          shareButtonWidget={true}
                          contributionId={content.contributionId}
                          scrollBarRef={scrollbarContentContainer}
                          scrollbarRef={scrollbarContentContainer}
                          widgetContentUrl={content.currentNew.canonicalUrl}
                          utmLink={utmLinks()}
                          handPickedNew={results.handPickedTopic}
                          contentContainer={contentContainer}
                          isFromPopup={isFromPopup}
                          notIncludeBranding={!includeBrandingProperty}
                          hideSummary={true}
                          embed
                          fallback={''}
                        />
                      );
                    })()}
                  </div>
                </CustomScrollbar>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export { useSubtextOneResource };
export default Embed;
